import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Users, UserPlus, Plus, Building2, CheckCircle2, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FloatingHelp, HELP_TOPICS } from '@/components/help';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useQueryClient } from '@tanstack/react-query';
import { useVolunteers, useInviteUser, useUpdateUserCapabilities, useTransferOwnership } from '@/hooks/queries';
import { useTeams, useDeleteTeam, useBulkAssignToTeams } from '@/hooks/queries/useTeams';
import { useVendors, useDeleteVendor } from '@/hooks/queries/useVendors';
import type { VendorResponse } from '@signalvote/shared';
import { useCampaignStore, useCoachmarkStore } from '@/stores';
import { useAuth } from '@/contexts/AuthContext';
import { queryKeys } from '@/lib/queryKeys';
import { CanvasserType } from '@signalvote/shared';
import type { InvitationResponse, PresetKey, UserCapabilities } from '@signalvote/shared';
import { PeopleFilters, PeopleTable, PeopleEmptyState, PeopleBulkActions, PendingInvites } from './people';
import { TeamsGrid } from './teams';
import { TeamDetailPanel } from './teams/TeamDetailPanel';
import { VendorsGrid, VendorDetailPanel, RenameVendorDialog } from './vendors';
import { CreateTeamDialog, ConfirmRemoveDialog, AssignToTeamDialog, InviteMemberDialog, EditCapabilitiesDialog } from './dialogs';
import { Coachmark } from '@/components/onboarding';
import type { CampaignUserDto } from '@signalvote/shared';

export function StaffPageContent() {
  const [activeView, setActiveView] = useState<string>('people');
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [vendorFilter, setVendorFilter] = useState<string | null>(null);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [detailPanelOpen, setDetailPanelOpen] = useState(false);

  const [createTeamOpen, setCreateTeamOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [assignToTeamOpen, setAssignToTeamOpen] = useState(false);
  const [deleteTeamId, setDeleteTeamId] = useState<string | null>(null);
  const [inviteFeedback, setInviteFeedback] = useState<InvitationResponse | null>(null);
  const [editCapabilitiesTarget, setEditCapabilitiesTarget] = useState<CampaignUserDto | null>(null);
  const [transferTarget, setTransferTarget] = useState<CampaignUserDto | null>(null);

  // Just-in-time spotlight on the Invite Member button: the first time a manager opens Staff for
  // this campaign, or when the onboarding "Add them" hand-off sends them here. Marked seen on
  // show (once per campaign), mirroring the turf walkthrough convention.
  const inviteButtonRef = useRef<HTMLButtonElement>(null);
  const campaignId = useCampaignStore((s) => s.selectedCampaign?.id);
  const coachmarkTarget = useCoachmarkStore((s) => s.target);
  const clearCoachmark = useCoachmarkStore((s) => s.clear);
  const [showInviteCoachmark, setShowInviteCoachmark] = useState(false);
  const inviteCoachmarkStartedRef = useRef(false);

  useEffect(() => {
    if (inviteCoachmarkStartedRef.current || !campaignId) return;
    const seenKey = `sv:invite-demo-seen:${campaignId}`;
    const forced = coachmarkTarget === 'invite-member';
    let seen = false;
    try {
      seen = localStorage.getItem(seenKey) === '1';
    } catch {
      /* ignore */
    }
    if (forced) clearCoachmark();
    if (forced || !seen) {
      inviteCoachmarkStartedRef.current = true;
      setShowInviteCoachmark(true);
      try {
        localStorage.setItem(seenKey, '1');
      } catch {
        /* ignore */
      }
    }
  }, [campaignId, coachmarkTarget, clearCoachmark]);

  // Vendor state
  const [selectedVendor, setSelectedVendor] = useState<VendorResponse | null>(null);
  const [vendorDetailOpen, setVendorDetailOpen] = useState(false);
  const [vendorCreateMode, setVendorCreateMode] = useState(false);
  const [deleteVendorId, setDeleteVendorId] = useState<string | null>(null);
  const [renameVendor, setRenameVendor] = useState<VendorResponse | null>(null);

  const { data: volunteers = [], isLoading: volunteersLoading } = useVolunteers();
  const { data: teams = [], isLoading: teamsLoading } = useTeams();
  const { data: vendors = [], isLoading: vendorsLoading } = useVendors();
  const { mutate: deleteTeam, isPending: deleteTeamPending } = useDeleteTeam();
  const { mutate: deleteVendorFn, isPending: deleteVendorPending } = useDeleteVendor();
  const { mutate: bulkAssign } = useBulkAssignToTeams();
  const {
    mutate: inviteUserFn,
    isPending: invitePending,
    error: inviteError,
    reset: resetInvite,
  } = useInviteUser();
  const {
    mutate: updateCapabilitiesFn,
    isPending: updateCapPending,
    error: updateCapError,
    reset: resetUpdateCap,
  } = useUpdateUserCapabilities();
  const { mutate: transferOwnershipFn, isPending: transferPending } = useTransferOwnership();
  const queryClient = useQueryClient();
  const isOwner = useCampaignStore((s) => s.isOwner());
  const { user } = useAuth();
  const canTransferOwnership = user?.isPlatformAdmin || isOwner;
  const canManageFinanceInCampaign = useCampaignStore((s) => s.canManage('Finance'));
  const canManageFinance = !!user?.isPlatformAdmin || canManageFinanceInCampaign;

  const filteredVolunteers = useMemo(() => {
    let result = volunteers;

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(
        (v: CampaignUserDto) =>
          v.firstName.toLowerCase().includes(query) ||
          v.lastName.toLowerCase().includes(query) ||
          v.email.toLowerCase().includes(query)
      );
    }

    if (roleFilter) {
      result = result.filter((v: CampaignUserDto) => {
        if (roleFilter === 'Owner') return v.isOwner;
        if (roleFilter === 'Custom') return !v.isOwner && !v.presetKey;
        return !v.isOwner && v.presetKey === roleFilter;
      });
    }

    if (statusFilter) {
      result = result.filter((v: CampaignUserDto) =>
        statusFilter === 'active' ? v.isActive : !v.isActive
      );
    }

    if (typeFilter) {
      result = result.filter((v: CampaignUserDto) => v.canvasserType === typeFilter);
    }

    if (vendorFilter) {
      result = result.filter((v: CampaignUserDto) => v.vendorId === vendorFilter);
    }

    return result;
  }, [volunteers, searchQuery, roleFilter, statusFilter, typeFilter, vendorFilter]);

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleToggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      const allIds = filteredVolunteers.map((v) => v.userId);
      const allSelected = allIds.every((id) => prev.has(id));
      if (allSelected) {
        return new Set();
      }
      return new Set(allIds);
    });
  }, [filteredVolunteers]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const handleTeamClick = (teamId: string) => {
    setSelectedTeamId(teamId);
    setDetailPanelOpen(true);
  };

  const confirmDeleteTeam = useCallback(() => {
    if (deleteTeamId) {
      deleteTeam(deleteTeamId, {
        onSuccess: () => setDeleteTeamId(null),
      });
    }
  }, [deleteTeamId, deleteTeam]);

  const deleteTeamName = useMemo(() => {
    if (!deleteTeamId) return '';
    const team = teams.find((t) => t.id === deleteTeamId);
    return team?.name ?? '';
  }, [deleteTeamId, teams]);

  const handleBulkAssign = useCallback((teamIds: string[]) => {
    bulkAssign(
      { teamIds, userIds: Array.from(selectedIds) },
      {
        onSuccess: () => {
          setAssignToTeamOpen(false);
          clearSelection();
        },
      }
    );
  }, [selectedIds, clearSelection, bulkAssign]);

  const handleInvite = useCallback((data: { email: string; presetKey: PresetKey | null; capabilities: UserCapabilities; canvasserType?: CanvasserType; vendorId?: string }) => {
    if (!campaignId) return;
    inviteUserFn(
      { campaignId, data: { email: data.email, presetKey: data.presetKey ?? undefined, capabilities: data.capabilities, canvasserType: data.canvasserType, vendorId: data.vendorId } },
      {
        onSuccess: (response) => {
          setInviteOpen(false);
          setInviteFeedback(response as InvitationResponse);
          setTimeout(() => setInviteFeedback(null), 5000);
          if (campaignId) {
            queryClient.invalidateQueries({
              queryKey: queryKeys.tenantCampaigns.invitations(campaignId),
            });
          }
        },
      }
    );
  }, [campaignId, inviteUserFn]);

  const handleSaveCapabilities = useCallback((data: { presetKey: PresetKey | null; capabilities: UserCapabilities; canvasserType: CanvasserType | undefined; vendorId: string | null }) => {
    if (!campaignId || !editCapabilitiesTarget) return;
    // Convert UserCapabilities to Record<string, number> for the API
    const capsRecord: Record<string, number> = {};
    for (const [key, value] of Object.entries(data.capabilities)) {
      if (value !== undefined) capsRecord[key] = value;
    }
    updateCapabilitiesFn(
      {
        campaignId,
        userId: editCapabilitiesTarget.userId,
        data: {
          ...(data.presetKey ? { presetKey: data.presetKey } : { capabilities: capsRecord }),
          ...(data.canvasserType
            ? { canvasserType: data.canvasserType, vendorId: data.vendorId }
            : { clearCanvasserType: true, vendorId: null }),
        },
      },
      {
        onSuccess: () => setEditCapabilitiesTarget(null),
      }
    );
  }, [campaignId, editCapabilitiesTarget, updateCapabilitiesFn]);

  const handleTransferOwnership = useCallback(() => {
    if (!campaignId || !transferTarget) return;
    transferOwnershipFn(
      { campaignId, newOwnerUserId: transferTarget.userId },
      {
        onSuccess: () => setTransferTarget(null),
      }
    );
  }, [campaignId, transferTarget, transferOwnershipFn]);

  const handleVendorClick = (vendorId: string) => {
    const vendor = vendors.find((v) => v.id === vendorId) ?? null;
    setSelectedVendor(vendor);
    setVendorDetailOpen(true);
  };

  const handleRenameVendor = (vendorId: string) => {
    const vendor = vendors.find((v) => v.id === vendorId) ?? null;
    setRenameVendor(vendor);
  };

  const confirmDeleteVendor = useCallback(() => {
    if (deleteVendorId) {
      deleteVendorFn(deleteVendorId, {
        onSuccess: () => setDeleteVendorId(null),
      });
    }
  }, [deleteVendorId, deleteVendorFn]);

  const deleteVendorName = useMemo(() => {
    if (!deleteVendorId) return '';
    const vendor = vendors.find((v) => v.id === deleteVendorId);
    return vendor?.name ?? '';
  }, [deleteVendorId, vendors]);

  const handleTabChange = (value: string) => {
    setActiveView(value);
    clearSelection();
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Staff</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage your campaign staff and vendors
          </p>
        </div>
        <div className="flex gap-2">
          {activeView === 'teams' && (
            <Button size="sm" onClick={() => setCreateTeamOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Team
            </Button>
          )}
          {activeView === 'vendors' && (
            <Button size="sm" onClick={() => { setSelectedVendor(null); setVendorCreateMode(true); setVendorDetailOpen(true); }}>
              <Plus className="h-4 w-4 mr-2" />
              Create Vendor
            </Button>
          )}
          <Button ref={inviteButtonRef} variant="outline" size="sm" onClick={() => setInviteOpen(true)}>
            <UserPlus className="h-4 w-4 mr-2" />
            Invite Member
          </Button>
        </div>
      </div>

      {inviteFeedback && (
        <div className="flex items-center gap-2 rounded-md border bg-muted/50 px-4 py-3 text-sm">
          {inviteFeedback.status === 'added' ? (
            <>
              <CheckCircle2 className="h-4 w-4 text-success" />
              <span>{inviteFeedback.message}</span>
            </>
          ) : (
            <>
              <Mail className="h-4 w-4 text-info" />
              <span>{inviteFeedback.message}</span>
            </>
          )}
          <button
            onClick={() => setInviteFeedback(null)}
            className="ml-auto text-muted-foreground hover:text-foreground"
          >
            &times;
          </button>
        </div>
      )}

      <Tabs value={activeView} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="people" className="gap-2">
            <Users className="h-4 w-4" />
            People
            {!volunteersLoading && (
              <span className="ml-1 rounded-full bg-muted px-2 py-0.5 text-xs">
                {volunteers.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="vendors" className="gap-2">
            <Building2 className="h-4 w-4" />
            Vendors
            {!vendorsLoading && (
              <span className="ml-1 rounded-full bg-muted px-2 py-0.5 text-xs">
                {vendors.length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="people" className="space-y-4 mt-4">
          <PendingInvites campaignId={campaignId} />

          <PeopleFilters
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            roleFilter={roleFilter}
            onRoleFilterChange={setRoleFilter}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            typeFilter={typeFilter}
            onTypeFilterChange={setTypeFilter}
            vendorFilter={vendorFilter}
            onVendorFilterChange={setVendorFilter}
            vendors={vendors}
          />

          {!volunteersLoading && volunteers.length === 0 ? (
            <PeopleEmptyState />
          ) : (
            <PeopleTable
              volunteers={filteredVolunteers}
              isLoading={volunteersLoading}
              selectedIds={selectedIds}
              onToggleSelect={handleToggleSelect}
              onToggleSelectAll={handleToggleSelectAll}
              onEditPermissions={setEditCapabilitiesTarget}
              onTransferOwnership={setTransferTarget}
              canTransferOwnership={canTransferOwnership}
              canManageFinance={canManageFinance}
              currentUserId={user?.id}
            />
          )}
        </TabsContent>

        <TabsContent value="vendors" className="mt-4">
          <VendorsGrid
            vendors={vendors}
            isLoading={vendorsLoading}
            onVendorClick={handleVendorClick}
            onDeleteVendor={setDeleteVendorId}
            onRenameVendor={handleRenameVendor}
            onCreateVendor={() => { setSelectedVendor(null); setVendorCreateMode(true); setVendorDetailOpen(true); }}
          />
        </TabsContent>
      </Tabs>

      <PeopleBulkActions
        selectedCount={selectedIds.size}
        onAssignToTeam={() => setAssignToTeamOpen(true)}
        onChangeRole={() => {
          // TODO: Implement bulk role change dialog
        }}
        onDeactivate={() => {
          // TODO: Implement bulk deactivate
        }}
        onClearSelection={clearSelection}
      />

      <TeamDetailPanel
        teamId={selectedTeamId}
        open={detailPanelOpen}
        onOpenChange={setDetailPanelOpen}
      />

      <CreateTeamDialog
        open={createTeamOpen}
        onOpenChange={setCreateTeamOpen}
      />

      <ConfirmRemoveDialog
        open={!!deleteTeamId}
        onOpenChange={(open) => { if (!open) setDeleteTeamId(null); }}
        title="Delete Team"
        description={`Delete "${deleteTeamName}"? All members will be removed from this team, but they won't be deleted from the campaign.`}
        confirmLabel="Delete Team"
        onConfirm={confirmDeleteTeam}
        isLoading={deleteTeamPending}
      />

      <AssignToTeamDialog
        open={assignToTeamOpen}
        onOpenChange={setAssignToTeamOpen}
        teams={teams}
        selectedUserIds={Array.from(selectedIds)}
        onAssign={handleBulkAssign}
      />

      <InviteMemberDialog
        open={inviteOpen}
        onOpenChange={(open) => {
          setInviteOpen(open);
          if (!open) resetInvite();
        }}
        onInvite={handleInvite}
        isPending={invitePending}
        error={inviteError ? inviteError.message : null}
      />

      <EditCapabilitiesDialog
        open={!!editCapabilitiesTarget}
        onOpenChange={(open) => {
          if (!open) {
            setEditCapabilitiesTarget(null);
            resetUpdateCap();
          }
        }}
        volunteer={editCapabilitiesTarget}
        onSave={handleSaveCapabilities}
        isPending={updateCapPending}
        error={updateCapError ? updateCapError.message : null}
      />

      <VendorDetailPanel
        vendor={selectedVendor}
        open={vendorDetailOpen}
        onOpenChange={(open) => { setVendorDetailOpen(open); if (!open) setVendorCreateMode(false); }}
        createMode={vendorCreateMode}
        onVendorCreated={(created) => { setSelectedVendor(created); setVendorCreateMode(false); }}
      />

      <RenameVendorDialog
        vendor={renameVendor}
        open={!!renameVendor}
        onOpenChange={(open) => { if (!open) setRenameVendor(null); }}
      />

      <ConfirmRemoveDialog
        open={!!deleteVendorId}
        onOpenChange={(open) => { if (!open) setDeleteVendorId(null); }}
        title="Delete Vendor"
        description={`Delete "${deleteVendorName}"? This action cannot be undone. Vendors with assigned canvassers or completed shifts cannot be deleted.`}
        confirmLabel="Delete Vendor"
        onConfirm={confirmDeleteVendor}
        isLoading={deleteVendorPending}
      />

      <ConfirmRemoveDialog
        open={!!transferTarget}
        onOpenChange={(open) => { if (!open) setTransferTarget(null); }}
        title="Transfer Ownership"
        description={`Transfer campaign ownership to ${transferTarget?.firstName} ${transferTarget?.lastName}? They will become the campaign owner with full control. The current owner will be downgraded to Full Admin.`}
        confirmLabel="Transfer Ownership"
        onConfirm={handleTransferOwnership}
        isLoading={transferPending}
      />

      <Coachmark
        active={showInviteCoachmark}
        targetRef={inviteButtonRef}
        message="Invite your team here — send each person an email invite to join your campaign."
        onDismiss={() => setShowInviteCoachmark(false)}
      />

      <FloatingHelp topic={HELP_TOPICS.staff} />
    </div>
  );
}
